import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://tenacitymusic.com';
const SITE_NAME = 'Tenacity Music';
const LOGO_URL = `${BASE_URL}/logo-tenacity.svg`;

// Website Schema - for global use
export function WebsiteJsonLd() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    description: 'Master jazz improvisation with world-class tutorials and courses from professional musicians.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/library?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Organization Schema - for global use
export function OrganizationJsonLd() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: SITE_NAME,
    url: BASE_URL,
    logo: LOGO_URL,
    description: 'Online jazz music education platform featuring world-class instructors and comprehensive courses.',
    sameAs: [
      'https://twitter.com/TenacityMusic',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      url: `${BASE_URL}/contact`,
    },
    areaServed: 'Worldwide',
    knowsAbout: ['Jazz', 'Music Education', 'Improvisation', 'Jazz Piano', 'Jazz Guitar', 'Jazz Drums', 'Jazz Bass'],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Breadcrumb Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Enhanced Video Schema with educational properties
interface VideoJsonLdProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: number; // in seconds
  contentUrl?: string;
  embedUrl?: string;
  instructor?: string;
  instructorUrl?: string;
  seriesName?: string;
  seriesUrl?: string;
  episodeNumber?: number;
  accessLevel?: string;
  tags?: string[];
}

export function VideoJsonLd({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration,
  contentUrl,
  embedUrl,
  instructor,
  instructorUrl,
  seriesName,
  seriesUrl,
  episodeNumber,
  accessLevel,
  tags,
}: VideoJsonLdProps) {
  const formatDuration = (seconds?: number) => {
    if (!seconds) return undefined;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `PT${hours > 0 ? `${hours}H` : ''}${minutes > 0 ? `${minutes}M` : ''}${secs > 0 ? `${secs}S` : ''}`;
  };

  const isFree = accessLevel === 'free';

  const videoSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl: thumbnailUrl.startsWith('http') ? thumbnailUrl : `${BASE_URL}${thumbnailUrl}`,
    uploadDate,
    duration: formatDuration(duration),
    contentUrl,
    embedUrl,
    inLanguage: 'en',
    isAccessibleForFree: isFree,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    ...(instructor && {
      author: {
        '@type': 'Person',
        name: instructor,
        url: instructorUrl ? (instructorUrl.startsWith('http') ? instructorUrl : `${BASE_URL}${instructorUrl}`) : undefined,
      },
    }),
    ...(seriesName && {
      isPartOf: {
        '@type': 'CreativeWorkSeries',
        name: seriesName,
        url: seriesUrl ? (seriesUrl.startsWith('http') ? seriesUrl : `${BASE_URL}${seriesUrl}`) : undefined,
      },
    }),
    ...(episodeNumber && { position: episodeNumber }),
    ...(tags && tags.length > 0 && { keywords: tags.join(', ') }),
  };

  // Also add LearningResource schema for educational SEO
  const learningSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    name,
    description,
    learningResourceType: 'Video Lesson',
    educationalLevel: accessLevel === 'mastery' ? 'Advanced' : accessLevel === 'pro' ? 'Intermediate' : 'Beginner',
    inLanguage: 'en',
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
    },
    ...(instructor && {
      author: { '@type': 'Person', name: instructor },
    }),
    ...(tags && tags.length > 0 && { keywords: tags.join(', ') }),
    about: [
      { '@type': 'Thing', name: 'Jazz' },
      { '@type': 'Thing', name: 'Music Education' },
    ],
    isAccessibleForFree: isFree,
  };

  const cleanVideo = JSON.parse(JSON.stringify(videoSchema));
  const cleanLearning = JSON.parse(JSON.stringify(learningSchema));

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(cleanVideo)}</script>
      <script type="application/ld+json">{JSON.stringify(cleanLearning)}</script>
    </Helmet>
  );
}

// Enhanced Course Schema for Series
interface CourseJsonLdProps {
  name: string;
  description: string;
  thumbnailUrl?: string;
  url: string;
  instructor?: string;
  instructorUrl?: string;
  numberOfEpisodes?: number;
  totalDurationSeconds?: number;
  dateCreated?: string;
  tags?: string[];
}

export function CourseJsonLd({
  name,
  description,
  thumbnailUrl,
  url,
  instructor,
  instructorUrl,
  numberOfEpisodes,
  totalDurationSeconds,
  dateCreated,
  tags,
}: CourseJsonLdProps) {
  const formatDuration = (seconds?: number) => {
    if (!seconds) return undefined;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `PT${hours > 0 ? `${hours}H` : ''}${minutes > 0 ? `${minutes}M` : ''}`;
  };

  const courseSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    url: url.startsWith('http') ? url : `${BASE_URL}${url}`,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
      logo: LOGO_URL,
    },
    ...(thumbnailUrl && {
      image: thumbnailUrl.startsWith('http') ? thumbnailUrl : `${BASE_URL}${thumbnailUrl}`,
    }),
    ...(instructor && {
      instructor: {
        '@type': 'Person',
        name: instructor,
        url: instructorUrl ? (instructorUrl.startsWith('http') ? instructorUrl : `${BASE_URL}${instructorUrl}`) : undefined,
      },
    }),
    ...(numberOfEpisodes && { numberOfCredits: numberOfEpisodes }),
    ...(totalDurationSeconds && { timeRequired: formatDuration(totalDurationSeconds) }),
    ...(dateCreated && { dateCreated }),
    ...(tags && tags.length > 0 && { keywords: tags.join(', ') }),
    inLanguage: 'en',
    isAccessibleForFree: false,
    educationalLevel: 'All Levels',
    about: [
      { '@type': 'Thing', name: 'Jazz' },
      { '@type': 'Thing', name: 'Music Improvisation' },
    ],
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: formatDuration(totalDurationSeconds),
    },
    offers: {
      '@type': 'Offer',
      category: 'Subscription',
      url: `${BASE_URL}/plans`,
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: SITE_NAME },
    },
  };

  // Also add ItemList for episode listing
  const itemListSchema: Record<string, unknown> | null = numberOfEpisodes && numberOfEpisodes > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${name} - Episodes`,
    numberOfItems: numberOfEpisodes,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
  } : null;

  const cleanCourse = JSON.parse(JSON.stringify(courseSchema));

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(cleanCourse)}</script>
      {itemListSchema && (
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      )}
    </Helmet>
  );
}

// Keep legacy SeriesJsonLd as alias
interface SeriesJsonLdProps {
  name: string;
  description: string;
  thumbnailUrl?: string;
  author?: string;
  numberOfEpisodes?: number;
}

export function SeriesJsonLd({
  name,
  description,
  thumbnailUrl,
  author,
  numberOfEpisodes,
}: SeriesJsonLdProps) {
  return (
    <CourseJsonLd
      name={name}
      description={description}
      thumbnailUrl={thumbnailUrl}
      url="/series"
      instructor={author}
      numberOfEpisodes={numberOfEpisodes}
    />
  );
}

// Enhanced Person/Artist Schema
interface PersonJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  url: string;
  jobTitle?: string;
  totalVideos?: number;
  totalSeries?: number;
  seriesNames?: string[];
}

export function PersonJsonLd({
  name,
  description,
  image,
  url,
  jobTitle = 'Jazz Musician & Instructor',
  totalVideos,
  totalSeries,
  seriesNames,
}: PersonJsonLdProps) {
  const personSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    description,
    image: image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : undefined,
    url: url.startsWith('http') ? url : `${BASE_URL}${url}`,
    jobTitle,
    worksFor: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
    },
    knowsAbout: ['Jazz', 'Music Education', 'Improvisation'],
    ...(seriesNames && seriesNames.length > 0 && {
      teaches: seriesNames.map(s => ({ '@type': 'Course', name: s })),
    }),
  };

  // Add ProfilePage schema
  const profileSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name,
      jobTitle,
      ...(image && { image: image.startsWith('http') ? image : `${BASE_URL}${image}` }),
    },
    name: `${name} - ${SITE_NAME}`,
    description: description || `Learn jazz from ${name} on ${SITE_NAME}`,
    url: url.startsWith('http') ? url : `${BASE_URL}${url}`,
    ...(totalVideos && { mainContentOfPage: { '@type': 'WebPageElement', name: `${totalVideos} video lessons` } }),
  };

  const cleanPerson = JSON.parse(JSON.stringify(personSchema));
  const cleanProfile = JSON.parse(JSON.stringify(profileSchema));

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(cleanPerson)}</script>
      <script type="application/ld+json">{JSON.stringify(cleanProfile)}</script>
    </Helmet>
  );
}

// FAQ Schema for FAQ page
interface FAQItem {
  question: string;
  answer: string;
}

export function FAQJsonLd({ items }: { items: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// WebPage Schema for homepage
interface WebPageJsonLdProps {
  name: string;
  description: string;
  url: string;
}

export function WebPageJsonLd({ name, description, url }: WebPageJsonLdProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: url.startsWith('http') ? url : `${BASE_URL}${url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL,
    },
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Product/Course Schema for Plans page
interface PlanOffer {
  name: string;
  description: string;
  price: number;
  priceCurrency?: string;
  billingPeriod: 'month' | 'year';
  features?: string[];
}

interface ProductOffersJsonLdProps {
  plans: PlanOffer[];
}

export function ProductOffersJsonLd({ plans }: ProductOffersJsonLdProps) {
  const schemas = plans.map((plan) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${plan.name} Membership`,
    description: plan.description,
    brand: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      price: plan.price,
      priceCurrency: plan.priceCurrency || 'USD',
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: 'https://schema.org/InStock',
      url: `${BASE_URL}/plans`,
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
    category: 'Online Music Education',
  }));

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}
